import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  ShoppingBag, 
  Search, 
  Eye, 
  MapPin, 
  Plus, 
  X, 
  User,
  Clock,
  Tag,
  Cpu,
  Wrench,
  Layers,
  Settings,
  FileText,
  Briefcase,
  Package,
  Trash2,
  Edit,
  ArrowLeft,
  MessageCircle,
  Upload,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { IMAGES, bgStyle } from '@/config/images';

// Icons for all disciplines
const categoryIcons = {
  // CNC Router
  cnc_machines: Cpu,
  bits_tooling: Wrench,
  materials: Layers,
  accessories: Settings,
  digital_files: FileText,
  services: Briefcase,
  other: Package,
  // 3D Printing
  '3d_printers': Cpu,
  filaments_resins: Layers,
  print_surfaces: Package,
  hotends_nozzles: Settings,
  upgrades_parts: Wrench,
  digital_stl: FileText,
  // Laser Cutting
  laser_cutters: Cpu,
  optics_lenses: Eye,
  laser_materials: Layers,
  safety_equipment: Package,
  exhaust_cooling: Settings,
  digital_designs: FileText,
};

const conditionLabels = {
  new: 'New',
  like_new: 'Like New',
  good: 'Good',
  fair: 'Fair'
};

// Discipline display names and colors
const disciplineConfig = {
  cnc_routing: {
    name: 'CNC Maker',
    color: 'orange',
    bgImage: IMAGES.MARKETPLACE
  },
  '3d_printing': {
    name: '3D Printing',
    color: 'orange',
    bgImage: IMAGES.MARKETPLACE
  },
  laser_cutting: {
    name: 'Laser Cutting',
    color: 'red',
    bgImage: IMAGES.MARKETPLACE
  }
};

const Marketplace = ({ discipline = 'cnc_routing' }) => {
  const { user } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [showMyListings, setShowMyListings] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    currency: 'USD',
    location: '',
    category: '',
    product_type: 'physical',
    condition: 'new',
    image_url: '',
    images: []  // Up to 5 images
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [failedImages, setFailedImages] = useState(new Set());
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token && showMyListings) {
      fetchMyListings();
    }
  }, [showMyListings, token, discipline]);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, currenciesRes] = await Promise.all([
        axios.get(`${API}/marketplace/products?discipline=${discipline}`),
        axios.get(`${API}/marketplace/categories?discipline=${discipline}`),
        axios.get(`${API}/marketplace/currencies`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setCurrencies(currenciesRes.data);
    } catch (error) {
      console.error('Failed to fetch marketplace data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMyListings = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/marketplace/my-listings?discipline=${discipline}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyListings(response.data);
    } catch (error) {
      console.error('Failed to fetch my listings:', error);
    }
  };

  // Helper to get currency symbol
  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency ? currency.symbol : code;
  };

  // Format price with currency
  const formatPrice = (price, currencyCode = 'USD') => {
    const symbol = getCurrencySymbol(currencyCode);
    return `${symbol}${price.toFixed(2)}`;
  };

  const fetchProducts = async (category = null, search = '') => {
    try {
      let url = `${API}/marketplace/products?discipline=${discipline}`;
      if (category) url += `&category=${category}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleSearch = () => {
    fetchProducts(selectedCategory, searchTerm);
  };

  const handleCategoryFilter = (categoryId) => {
    const newCategory = selectedCategory === categoryId ? null : categoryId;
    setSelectedCategory(newCategory);
    fetchProducts(newCategory, searchTerm);
  };

  const handlePurchase = async () => {
    toast.error('Marketplace payments are temporarily unavailable.');
  };

  const handleProductClick = async (product) => {
    try {
      const response = await axios.get(`${API}/marketplace/products/${product.id}`);
      setSelectedProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMessageSeller = async (product) => {
    if (!token) {
      toast.error('Please login to message the seller');
      return;
    }
    if (!product?.id) return;

    try {
      const res = await axios.post(
        `${API}/messages/conversations`,
        { product_id: product.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.assign(`/messages?conversation=${encodeURIComponent(res.data.id)}`);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (msg && msg.toLowerCase().includes('cannot message yourself')) {
        toast.error('You cannot message yourself. This is your listing.');
      } else {
        toast.error('Failed to start conversation');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: '',
      currency: 'USD',
      location: '',
      category: '',
      discipline: discipline,
      product_type: 'physical',
      condition: 'new',
      image_url: '',
      images: []
    });
    setEditingProduct(null);
  };

  const handleSubmitListing = async () => {
    if (!formData.title || !formData.price || !formData.category || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        discipline: discipline,
        price: parseFloat(formData.price)
      };

      if (editingProduct) {
        await axios.put(`${API}/marketplace/products/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Listing updated successfully!');
      } else {
        await axios.post(`${API}/marketplace/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Listing created successfully!');
      }

      setShowSellModal(false);
      resetForm();
      fetchProducts();
      if (showMyListings) fetchMyListings();
    } catch (error) {
      toast.error('Failed to save listing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditListing = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description,
      price: product.price.toString(),
      currency: product.currency || 'USD',
      location: product.location || '',
      category: product.category,
      product_type: product.product_type || 'physical',
      condition: product.condition || 'new',
      image_url: product.image_url || '',
      images: product.images || []
    });
    setShowSellModal(true);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check max images limit
    const currentCount = formData.images.length;
    const availableSlots = 5 - currentCount;
    
    if (availableSlots <= 0) {
      toast.error('Maximum 5 images allowed per listing');
      return;
    }

    const filesToUpload = files.slice(0, availableSlots);
    if (files.length > availableSlots) {
      toast.info(`Only uploading ${availableSlots} image(s). Maximum 5 images allowed.`);
    }

    setUploadingImage(true);
    const newImages = [];
    let totalSaved = 0;

    for (const file of filesToUpload) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }

      // Validate file size (5MB max - server will compress)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum 5MB per image.`);
        continue;
      }

      try {
        // Convert to base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Upload to server (server handles compression)
        const response = await axios.post(
          `${API}/marketplace/upload-image`,
          { image_data: base64, filename: file.name },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        newImages.push(response.data.url);
        
        // Track compression savings
        if (response.data.compression_ratio > 0) {
          totalSaved += (response.data.original_size_kb - response.data.compressed_size_kb);
        }
      } catch (error) {
        console.error('Failed to upload image:', error);
        const detail = error.response?.data?.detail || error.message || 'Unknown error';
        toast.error(`Failed to upload ${file.name}: ${detail}`);
      }
    }

    if (newImages.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newImages],
        // Set first image as main image_url if not set
        image_url: prev.image_url || newImages[0]
      }));
      
      // Show success message with compression info
      if (totalSaved > 0) {
        toast.success(`${newImages.length} image(s) uploaded • ${Math.round(totalSaved)}KB saved via compression`);
      } else {
        toast.success(`${newImages.length} image(s) uploaded successfully`);
      }
    }

    setUploadingImage(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove an image
  const handleRemoveImage = (indexToRemove) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, index) => index !== indexToRemove);
      // Update image_url if the removed image was the main one
      const removedUrl = prev.images[indexToRemove];
      let newImageUrl = prev.image_url;
      if (prev.image_url === removedUrl) {
        newImageUrl = newImages.length > 0 ? newImages[0] : '';
      }
      return {
        ...prev,
        images: newImages,
        image_url: newImageUrl
      };
    });
  };

  // Set an image as the main image
  const handleSetMainImage = (url) => {
    setFormData(prev => ({ ...prev, image_url: url }));
    toast.success('Main image updated');
  };

  const handleDeleteListing = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      await axios.delete(`${API}/marketplace/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Listing deleted');
      fetchProducts();
      fetchMyListings();
    } catch (error) {
      toast.error('Failed to delete listing');
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : categoryId;
  };

  // State for image gallery in product detail
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Get all images for a product (combine image_url and images array)
  const getProductImages = (product) => {
    const images = [];
    if (product.images && product.images.length > 0) {
      images.push(...product.images);
    } else if (product.image_url) {
      images.push(product.image_url);
    }
    return images;
  };

  // Resolve image URL (handles /api-prefixed paths from upload endpoint)
  const getImageSrc = (url) => {
    if (!url) return null;
    if (url.startsWith('/api')) {
      return `${API.replace('/api', '')}${url}`;
    }
    return url;
  };

  // Product Detail View
  if (selectedProduct) {
    const CategoryIcon = categoryIcons[selectedProduct.category] || Package;
    const productImages = getProductImages(selectedProduct);
    
    return (
      <div data-testid="product-detail" className="max-w-5xl mx-auto space-y-6">
        <Button
          data-testid="back-to-marketplace-btn"
          variant="ghost"
          onClick={() => { setSelectedProduct(null); setActiveImageIndex(0); }}
          className="text-[#78716C] hover:text-[#292524]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Image Section - 3 cols */}
          <div className="lg:col-span-3 space-y-3">
            {/* Main Image */}
            <div className="relative">
              {productImages.length > 0 ? (
                <>
                  <img
                    src={getImageSrc(productImages[activeImageIndex])}
                    alt={selectedProduct.title}
                    className="w-full h-[250px] md:h-[400px] object-cover rounded-xl border border-[#D6D3D1]"
                  />
                  {/* Navigation arrows */}
                  {productImages.length > 1 && (
                    <>
                      <button
                        onClick={() => setActiveImageIndex(prev => prev === 0 ? productImages.length - 1 : prev - 1)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={() => setActiveImageIndex(prev => prev === productImages.length - 1 ? 0 : prev + 1)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                      {/* Image counter */}
                      <div className="absolute bottom-3 right-3 bg-black/60 text-white text-sm px-2 py-1 rounded">
                        {activeImageIndex + 1} / {productImages.length}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-[250px] md:h-[400px] bg-white/80 backdrop-blur-sm rounded-xl border border-[#D6D3D1] flex items-center justify-center">
                  <ShoppingBag className="w-24 h-24 text-[#57534E]" />
                </div>
              )}
            </div>

            {/* Thumbnail Strip */}
            {productImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {productImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${
                      idx === activeImageIndex ? 'border-[#e67e22] ring-2 ring-[#e67e22]/30' : 'border-[#D6D3D1] hover:border-[#B45309]'
                    }`}
                  >
                    <img
                      src={getImageSrc(img)}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info Section - 2 cols */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
              <CardContent className="pt-6 space-y-4">
                {/* Price */}
                <div className="text-4xl font-bold text-[#10B981]">
                  {formatPrice(selectedProduct.price, selectedProduct.currency)}
                  <span className="text-lg text-[#78716C] ml-2">{selectedProduct.currency || 'USD'}</span>
                </div>
                
                {/* Title */}
                <h1 className="text-2xl font-bold text-[#292524]">{selectedProduct.title}</h1>
                
                {/* Location & Time */}
                <div className="flex flex-wrap gap-3 text-sm text-[#78716C]">
                  {selectedProduct.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {selectedProduct.location}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatTimeAgo(selectedProduct.created_at)}
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {selectedProduct.views} views
                  </div>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-[#B45309]/20 text-[#B45309] border border-[#F59E0B]/30">
                    <CategoryIcon className="w-3 h-3 mr-1" />
                    {getCategoryName(selectedProduct.category)}
                  </Badge>
                  <Badge variant="outline" className="text-[#78716C] border-gray-600">
                    {conditionLabels[selectedProduct.condition] || selectedProduct.condition}
                  </Badge>
                  {selectedProduct.product_type === 'digital' && (
                    <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">
                      Digital Item
                    </Badge>
                  )}
                </div>

                {/* Purchase Button */}
                {user?.id !== selectedProduct.seller_id && (
                  <Button
                    data-testid="purchase-btn"
                    onClick={() => handlePurchase(selectedProduct)}
                    disabled={purchasing}
                    className="w-full bg-[#10B981] hover:bg-[#10B981]/90 text-[#292524] font-bold py-6 text-lg"
                  >
                    {purchasing ? 'Processing...' : 'Buy Now'}
                  </Button>
                )}

                {/* Contact Seller */}
                <Button
                  variant="outline"
                  className="w-full border-[#D6D3D1] text-[#57534E] hover:bg-[#334155]"
                  onClick={() => handleMessageSeller(selectedProduct)}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Message Seller
                </Button>
              </CardContent>
            </Card>

            {/* Seller Info */}
            <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#B45309]/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-[#B45309]" />
                  </div>
                  <div>
                    <div className="text-[#292524] font-medium">{selectedProduct.seller_name}</div>
                    <div className="text-[#78716C] text-sm">{selectedProduct.sales_count} items sold</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Description */}
        <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
          <CardHeader>
            <CardTitle className="text-[#292524]">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#57534E] whitespace-pre-wrap leading-relaxed">
              {selectedProduct.description}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Marketplace View
  // Get discipline-specific config
  const config = disciplineConfig[discipline] || disciplineConfig.cnc_routing;
  const accentColor = discipline === 'laser_cutting' ? '#ef4444' : '#e67e22';

  return (
    <div 
      data-testid="marketplace-page" 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(config.bgImage || IMAGES.MARKETPLACE)}
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
      <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: accentColor }}>BUY & SELL</div>
              <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tight mb-2" style={{ color: accentColor }}>
                {config.name.toUpperCase()} MARKETPLACE
              </h1>
              <p className="text-lg" style={{ color: accentColor }}>
                {discipline === 'cnc_routing' && 'Buy and sell CNC equipment, materials, and projects'}
                {discipline === '3d_printing' && 'Buy and sell 3D printers, filaments, and printed parts'}
                {discipline === 'laser_cutting' && 'Buy and sell laser cutters, materials, and designs'}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2">
              {token && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setShowMyListings(!showMyListings)}
                    className={`border-[#3a3a3a] ${showMyListings ? 'text-black' : 'text-[#d6d6d6]'}`}
                    style={showMyListings ? { backgroundColor: accentColor } : {}}
                  >
                    <User className="w-4 h-4 mr-2" />
                    My Listings
                  </Button>
                  <Dialog open={showSellModal} onOpenChange={(open) => { setShowSellModal(open); if (!open) resetForm(); }}>
                    <DialogTrigger asChild>
                      <Button 
                        className="text-black font-bold hover:opacity-90"
                        style={{ backgroundColor: accentColor }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Sell Item
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#1f1f1f] border-[#3a3a3a] max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-white text-xl">
                          {editingProduct ? 'Edit Listing' : 'Create New Listing'}
                        </DialogTitle>
                        <DialogDescription className="text-[#a3a3a3]">
                          Fill in the details to list your item for sale
                        </DialogDescription>
                      </DialogHeader>
                      
                      <div className="space-y-4 mt-4">
                        {/* Title */}
                        <div>
                          <label className="text-sm text-[#a3a3a3] mb-1 block">Title *</label>
                          <Input
                            placeholder="What are you selling?"
                            value={formData.title}
                            onChange={(e) => handleFormChange('title', e.target.value)}
                            className="bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3]"
                          />
                        </div>

                        {/* Price, Currency & Location Row */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="text-sm text-[#a3a3a3] mb-1 block">Price *</label>
                            <Input
                              type="number"
                              placeholder="0.00"
                              value={formData.price}
                              onChange={(e) => handleFormChange('price', e.target.value)}
                              className="bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3]"
                            />
                      </div>
                      <div>
                        <label className="text-sm text-[#78716C] mb-1 block">Currency *</label>
                        <Select value={formData.currency} onValueChange={(v) => handleFormChange('currency', v)}>
                          <SelectTrigger className="bg-white/60 border-[#D6D3D1] text-[#292524]">
                            <SelectValue placeholder="USD" />
                          </SelectTrigger>
                          <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1] max-h-[200px]">
                            {currencies.map(cur => (
                              <SelectItem key={cur.code} value={cur.code}>
                                {cur.symbol} {cur.code} - {cur.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm text-[#78716C] mb-1 block">Location *</label>
                        <Input
                          placeholder="City, State"
                          value={formData.location}
                          onChange={(e) => handleFormChange('location', e.target.value)}
                          className="bg-white/60 border-[#D6D3D1] text-[#292524]"
                        />
                      </div>
                    </div>

                    {/* Category */}
                    <div>
                      <label className="text-sm text-[#78716C] mb-1 block">Category *</label>
                      <Select value={formData.category} onValueChange={(v) => handleFormChange('category', v)}>
                        <SelectTrigger className="bg-white/60 border-[#D6D3D1] text-[#292524]">
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Condition & Type Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-[#78716C] mb-1 block">Condition</label>
                        <Select value={formData.condition} onValueChange={(v) => handleFormChange('condition', v)}>
                          <SelectTrigger className="bg-white/60 border-[#D6D3D1] text-[#292524]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="like_new">Like New</SelectItem>
                            <SelectItem value="good">Good</SelectItem>
                            <SelectItem value="fair">Fair</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm text-[#78716C] mb-1 block">Type</label>
                        <Select value={formData.product_type} onValueChange={(v) => handleFormChange('product_type', v)}>
                          <SelectTrigger className="bg-white/60 border-[#D6D3D1] text-[#292524]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
                            <SelectItem value="physical">Physical Item</SelectItem>
                            <SelectItem value="digital">Digital File</SelectItem>
                            <SelectItem value="service">Service</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Image Upload Section */}
                    <div>
                      <label className="text-sm text-[#78716C] mb-2 block">
                        Product Images (up to 5)
                        <span className="text-xs text-[#a3a3a3] ml-2">Max 5MB each • Auto-compressed • JPG, PNG, WebP</span>
                      </label>
                      
                      {/* Upload Button */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/*"
                        multiple
                        className="hidden"
                      />
                      
                      {formData.images.length < 5 && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingImage}
                          className="w-full mb-3 border-dashed border-2 border-[#D6D3D1] bg-white/30 hover:bg-white/50 text-[#78716C]"
                        >
                          {uploadingImage ? (
                            <>Uploading...</>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Click to Upload Images ({formData.images.length}/5)
                            </>
                          )}
                        </Button>
                      )}

                      {/* Image Preview Grid */}
                      {formData.images.length > 0 && (
                        <div className="grid grid-cols-5 gap-2 mt-2">
                          {formData.images.map((url, index) => (
                            <div 
                              key={index} 
                              className={`relative group aspect-square rounded-md overflow-hidden border-2 ${
                                formData.image_url === url ? 'border-[#e67e22]' : 'border-[#D6D3D1]'
                              }`}
                            >
                              <img
                                src={url.startsWith('/api') ? `${API.replace('/api', '')}${url}` : url}
                                alt={`Product ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              {/* Main badge */}
                              {formData.image_url === url && (
                                <div className="absolute top-0 left-0 bg-[#e67e22] text-white text-[10px] px-1 py-0.5 rounded-br">
                                  Main
                                </div>
                              )}
                              {/* Hover overlay with actions */}
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                                {formData.image_url !== url && (
                                  <button
                                    type="button"
                                    onClick={() => handleSetMainImage(url)}
                                    className="text-xs text-white bg-[#e67e22] px-2 py-1 rounded hover:bg-[#d35400]"
                                  >
                                    Set Main
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveImage(index)}
                                  className="text-xs text-white bg-red-500 px-2 py-1 rounded hover:bg-red-600"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Empty state */}
                      {formData.images.length === 0 && (
                        <div className="text-center py-4 text-sm text-[#a3a3a3] border border-dashed border-[#D6D3D1] rounded-md bg-white/20">
                          <ImageIcon className="w-8 h-8 mx-auto mb-2 text-[#D6D3D1]" />
                          No images uploaded yet
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <label className="text-sm text-[#78716C] mb-1 block">Description</label>
                      <Textarea
                        placeholder="Describe your item in detail..."
                        value={formData.description}
                        onChange={(e) => handleFormChange('description', e.target.value)}
                        className="bg-white/60 border-[#D6D3D1] text-[#292524] min-h-[100px]"
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      onClick={handleSubmitListing}
                      disabled={submitting || uploadingImage}
                      className="w-full bg-[#B45309] hover:bg-[#B45309]/90 text-[#0F172A] font-bold"
                    >
                      {submitting ? 'Saving...' : (editingProduct ? 'Update Listing' : 'Post Listing')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
        <CardContent className="pt-6 space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#78716C]" />
              <Input
                data-testid="marketplace-search-input"
                type="text"
                placeholder="Search items, location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-white/60 border-[#D6D3D1] text-[#292524]"
              />
            </div>
            <Button
              data-testid="marketplace-search-btn"
              onClick={handleSearch}
              className="bg-[#B45309] hover:bg-[#B45309]/90 text-[#0F172A] font-bold"
            >
              Search
            </Button>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleCategoryFilter(null)}
              className={selectedCategory === null 
                ? 'bg-[#B45309] hover:bg-[#B45309]/90 text-black' 
                : 'border-[#D6D3D1] text-[#78716C] hover:text-[#292524]'
              }
            >
              All
            </Button>
            {categories.map(cat => {
              const Icon = categoryIcons[cat.id] || Package;
              return (
                <Button
                  key={cat.id}
                  variant="outline"
                  size="sm"
                  onClick={() => handleCategoryFilter(cat.id)}
                  className={selectedCategory === cat.id 
                    ? 'bg-[#B45309]/20 border-[#F59E0B] text-[#B45309]' 
                    : 'border-[#D6D3D1] text-[#78716C] hover:text-[#292524]'
                  }
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {cat.name}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* My Listings Section */}
      {showMyListings && (
        <div className="space-y-4">
          {myListings.length === 0 && (
            <div className="text-[#78716C]">No listings yet. Click “Sell Item” to create one.</div>
          )}
          <h2 className="text-xl font-bold text-[#292524]">My Listings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {myListings.map((product) => (
              <Card 
                key={product.id}
                className="bg-white/80 backdrop-blur-sm border-[#D6D3D1] overflow-hidden"
              >
                <div className="relative">
                  {getImageSrc(getProductImages(product)[0]) && !failedImages.has(`my-${product.id}`) ? (
                    <img
                      src={getImageSrc(getProductImages(product)[0])}
                      alt={product.title}
                      className="w-full h-40 object-cover"
                      onError={() => setFailedImages(prev => new Set(prev).add(`my-${product.id}`))}
                    />
                  ) : (
                    <div className="w-full h-40 bg-white/60 flex items-center justify-center">
                      <ShoppingBag className="w-12 h-12 text-[#57534E]" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1 z-10">
                    <Button
                      size="sm"
                      variant="secondary"
                      data-testid={`mylisting-edit-${product.id}`}
                      className="h-8 w-8 p-0 bg-black/60 hover:bg-black/80"
                      onClick={() => handleEditListing(product)}
                    >
                      <Edit className="w-4 h-4 text-white" />
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      data-testid={`mylisting-delete-${product.id}`}
                      className="h-8 w-8 p-0 bg-black/60 hover:bg-red-500/70"
                      onClick={() => handleDeleteListing(product.id)}
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-3">
                  <div className="text-lg font-bold text-[#10B981]">{formatPrice(product.price, product.currency)}</div>
                  <div className="text-[#292524] font-medium truncate">{product.title}</div>
                  <div className="text-[#78716C] text-sm flex items-center gap-1 mt-1">
                    <Eye className="w-3 h-3" /> {product.views} views
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <hr className="border-[#D6D3D1]" />
        </div>
      )}

      {/* Products Grid - FB Marketplace Style */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {products.map((product) => {
          const CategoryIcon = categoryIcons[product.category] || Package;
          return (
            <Card
              key={product.id}
              data-testid={`product-${product.id}`}
              onClick={() => handleProductClick(product)}
              className="bg-white/80 backdrop-blur-sm border-[#D6D3D1] hover:border-[#F59E0B]/50 transition-all cursor-pointer group overflow-hidden"
            >
              {/* Image */}
              <div className="relative">
                {getImageSrc(getProductImages(product)[0]) && !failedImages.has(product.id) ? (
                  <img
                    src={getImageSrc(getProductImages(product)[0])}
                    alt={product.title}
                    className="w-full h-44 object-cover group-hover:scale-105 transition-transform"
                    onError={() => setFailedImages(prev => new Set(prev).add(product.id))}
                  />
                ) : (
                  <div className="w-full h-44 bg-white/60 flex items-center justify-center">
                    <ShoppingBag className="w-12 h-12 text-[#57534E]" />
                  </div>
                )}
                {/* Category Badge */}
                <div className="absolute top-2 left-2">
                  <Badge className="bg-black/60 text-[#292524] text-xs">
                    <CategoryIcon className="w-3 h-3 mr-1" />
                    {getCategoryName(product.category)}
                  </Badge>
                </div>
              </div>

              {/* Content */}
              <CardContent className="p-3 space-y-1">
                {/* Price */}
                <div className="text-xl font-bold text-[#10B981]">
                  {formatPrice(product.price, product.currency)}
                </div>
                
                {/* Title */}
                <h3 className="text-[#292524] font-medium line-clamp-2 group-hover:text-[#B45309] transition-colors">
                  {product.title}
                </h3>
                
                {/* Location & Time */}
                <div className="flex items-center gap-2 text-xs text-[#78716C]">
                  {product.location && (
                    <>
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{product.location}</span>
                      <span>·</span>
                    </>
                  )}
                  <span>{formatTimeAgo(product.created_at)}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {products.length === 0 && !isLoading && (
        <div className="text-center py-16">
          <ShoppingBag className="w-20 h-20 text-[#a3a3a3] mx-auto mb-4" />
          <p className="text-[#d6d6d6] text-lg">No listings found</p>
          <p className="text-[#a3a3a3] text-sm mt-1">Try adjusting your search or filters</p>
          {token && (
            <Button
              onClick={() => setShowSellModal(true)}
              className="mt-4 bg-[#e67e22] hover:bg-[#e67e22]/90 text-black font-bold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Be the first to sell
            </Button>
          )}
        </div>
      )}
        </div>
    </div>
  );
};

export default Marketplace;
